// src/context/AchievementContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { ALL_DEFINITIONS } from '../data/medalsDefinitions';
import { checkCondition, createEmptyProgress } from '../utils/conditionEngine';
import { getToken } from '../utils/secureStorage';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const AchievementContext = createContext();

// ============================================================
//  توابع کمکی برای کار با سرور (به‌جای localStorage)
// ============================================================
const fetchAchievementsFromServer = async (userId) => {
  try {
    const token = getToken();
    const res = await fetch(`${API_BASE}/achievements?userId=${encodeURIComponent(userId)}`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {}
    });
    const data = await res.json();
    if (Array.isArray(data) && data.length > 0) {
      return { record: data[0], progress: data[0].progress };
    }
    return { record: null, progress: null };
  } catch (e) {
    console.warn('⚠️ AchievementContext fetch failed:', e);
    return { record: null, progress: null };
  }
};

const saveAchievementsToServer = async (userId, progress, recordId) => {
  try {
    const token = getToken();
    const authHeaders = token ? { 'Authorization': `Bearer ${token}` } : {};

    if (recordId) {
      await fetch(`${API_BASE}/achievements/${recordId}`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({ progress }),
      });
      return { id: recordId };
    } else {
      const res = await fetch(`${API_BASE}/achievements`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({ userId, progress }),
      });
      const data = await res.json().catch(() => null);
      return data;
    }
  } catch (e) {
    console.warn('⚠️ AchievementContext save failed:', e);
    return null;
  }
};

const deleteAchievementsFromServer = async (recordId) => {
  if (!recordId) return;
  try {
    const token = getToken();
    await fetch(`${API_BASE}/achievements/${recordId}`, { 
      method: 'DELETE',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {}
    });
  } catch (e) {
    console.warn('⚠️ AchievementContext delete failed:', e);
  }
};

export function AchievementProvider({ children }) {
  const { user } = useAuth();
  const [userProgress, setUserProgress] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

// ✅ نگه‌داشتن id رکورد achievements برای PATCH سریع
const recordIdRef = useRef(null);

useEffect(() => {
  let cancelled = false;
  const load = async () => {
    if (!user) {
      recordIdRef.current = null;
      setUserProgress(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const { record, progress } = await fetchAchievementsFromServer(user.id);
    if (cancelled) return;
    recordIdRef.current = record?.id || null;
    if (progress) {
      // اطمینان از وجود فیلدهای لازم
      if (!progress.history) progress.history = {};
      if (!progress.counters) progress.counters = {};
      if (!progress.lastEventDate) progress.lastEventDate = {};
      setUserProgress(progress);
    } else {
      // رکورد اولیه می‌سازیم — ذخیره‌ی واقعی در save-effect انجام می‌شود
      // تا از POST تکراری جلوگیری شود
      const empty = createEmptyProgress();
      setUserProgress(empty);
    }
    setIsLoading(false);
  };
  load();
  return () => { cancelled = true; };
}, [user]);

// ============================================================
//  ذخیره‌ی خودکار پیشرفت مدال‌ها در سرور (بعد از هر تغییر)
// ============================================================
const isFirstRenderRef = useRef(true);
useEffect(() => {
  if (isFirstRenderRef.current) {
    isFirstRenderRef.current = false;
    return;
  }
  if (user && userProgress) {
    saveAchievementsToServer(user.id, userProgress, recordIdRef.current).then((rec) => {
      // 🔧 FIX: بعد از POST موفق، id رکورد جدید را ذخیره کن
      // تا save های بعدی به جای POST، PATCH بزنند
      if (rec?.id && !recordIdRef.current) {
        recordIdRef.current = rec.id;
      }
    });
  }
}, [user, userProgress]);

  const emitEvent = useCallback((eventType, payload = {}) => {
    if (!user || !userProgress) return;

    const newProgress = JSON.parse(JSON.stringify(userProgress));

    const value = payload.value !== undefined ? payload.value : 1;
    newProgress.counters[eventType] = (newProgress.counters[eventType] || 0) + value;

    if (payload.record !== false) {
      const entry = {
        date: new Date().toISOString().split('T')[0],
        ...payload,
      };
      if (!newProgress.history[eventType]) newProgress.history[eventType] = [];
      newProgress.history[eventType].push(entry);
      newProgress.lastEventDate[eventType] = entry.date;
    }

    let newlyUnlocked = [];
    ALL_DEFINITIONS.forEach(def => {
      if (newProgress.unlocked.includes(def.id)) return;
      const { isMet, progress } = checkCondition(def.condition, newProgress);
      if (isMet) {
        newProgress.unlocked.push(def.id);
        newProgress.progress[def.id] = 100;
        newlyUnlocked.push(def);
      } else {
        newProgress.progress[def.id] = progress;
      }
    });

    setUserProgress(newProgress);

    if (newlyUnlocked.length > 0) {
      window.dispatchEvent(new CustomEvent('medalUnlocked', {
        detail: { medals: newlyUnlocked }
      }));
    }
  }, [user, userProgress]);

const resetProgress = useCallback(() => {
  if (!user) return;
  // ✅ حذف رکورد فعلی از سرور
  deleteAchievementsFromServer(recordIdRef.current).then(() => {
    recordIdRef.current = null;
  });
  // ✅ ساخت یک progress خالی جدید
  const empty = createEmptyProgress();
  setUserProgress(empty);
  // ✅ ذخیره نسخه خالی جدید در سرور
  saveAchievementsToServer(user.id, empty, null).then(() => {
    fetchAchievementsFromServer(user.id).then(({ record }) => {
      if (record) recordIdRef.current = record.id;
    });
  });
}, [user]);

  const getMedalStatus = useCallback((medalId) => {
    if (!userProgress) return { unlocked: false, progress: 0 };
    return {
      unlocked: userProgress.unlocked.includes(medalId),
      progress: userProgress.progress[medalId] || 0,
    };
  }, [userProgress]);

  const getAllMedalsWithStatus = useCallback(() => {
    if (!userProgress) return ALL_DEFINITIONS.map(d => ({ ...d, unlocked: false, progress: 0 }));
    return ALL_DEFINITIONS.map(def => ({
      ...def,
      unlocked: userProgress.unlocked.includes(def.id),
      progress: userProgress.progress[def.id] || 0,
    }));
  }, [userProgress]);

  return (
    <AchievementContext.Provider value={{
      userProgress,
      isLoading,
      emitEvent,
      resetProgress,
      getMedalStatus,
      getAllMedalsWithStatus,
    }}>
      {children}
    </AchievementContext.Provider>
  );
}

export function useAchievements() {
  const context = useContext(AchievementContext);
  if (!context) throw new Error('useAchievements must be used within AchievementProvider');
  return context;
}